import React, { useEffect, useState } from 'react';
import './SignUp.scss';
import { TwButton, TwButtonType } from '../../components/TwButtons/TwButtons';
import { TwTitle, TwTitleColor, TwTitleType } from '../../components/TwTitles/TwTitles';
import { ReactComponent as TruckbaseLogo } from '../../assets/truckbase-logo.svg';
import { ReactComponent as TbLogo } from '../../assets/tb-logo.svg';
import TwField, { TwInputTypes } from '../../components/TwFields/TwFields';
import { useFormik } from 'formik';
import AuthService from '../../providers/Auth/Auth.service';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { SignUpData } from '../../providers/Auth/Auth.model';
import useAuth from '../../providers/Auth/Auth.provider';
import { addCohereIdentity } from '../../utils/cohereUtils';
import { addLogRocketIdentity } from '../../utils/logRocketUtils';
import usePageTitle from '../../hooks/usePageTitle';
import useFeatureFlags from '../../providers/FeatureFlags/FeatureFlags.provider';
import { USER_ROLE } from '../../providers/User/User.model';
import { SignUpFormSchema } from './SignUp.validator';
import useNotifications from '../../providers/Notifications/Notifications.provider';

interface SignUpFormValues {
  firstName: string;
  lastName: string;
  companyName: string;
  email: string;
  phone: string;
  usdot: string;
  password: string;
}

const emptyValues: SignUpFormValues = {
  firstName: '',
  lastName: '',
  companyName: '',
  email: '',
  phone: '',
  usdot: '',
  password: ''
};

const SignUp: React.FC = (): JSX.Element => {
  usePageTitle('Sign up | Truckbase');

  const privacyUrl =
    'https://uploads-ssl.webflow.com/64183b5795f52c46334f8d02/642da08cc6e27cdbffab3b86_TruckBase%20-%20Privacy%20Policy.pdf';
  const termsUrl =
    'https://uploads-ssl.webflow.com/64183b5795f52c46334f8d02/642da087a0ad300bee887138_TruckBase%20-%20Terms%20of%20Service.pdf';
  const { updateDataLoading, updateAuthData, updateUserData } = useAuth();
  const navigate = useNavigate();
  const { enableShowWelcome } = useFeatureFlags();
  const [tokenNotFound, setTokenNotFound] = useState(true);
  const [role, setRole] = useState<USER_ROLE | undefined>(undefined);

  const { addNotification } = useNotifications();

  // Invited token and user
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get('token');

  useEffect(() => {
    inviteToken &&
      AuthService.getInvitedUser(inviteToken)
        .then((invited) => {
          setRole(invited.role);
          setValues({
            firstName: invited.first_name,
            lastName: invited.last_name,
            companyName: invited.tenant.company_name,
            email: invited.email_address,
            phone: invited.phone,
            usdot: invited.tenant.usdot_number || '',
            password: ''
          });

          setTokenNotFound(false);
        })
        .catch((error) => {
          if (error === 400) {
            navigate('/expired-invite');
          }

          if (error === 404) {
            navigate('/already-signed-up');
          }
        });
  }, [inviteToken]);

  const signUpSubmit = (values: SignUpData): void => {
    updateDataLoading(true);

    AuthService.signUp(values, inviteToken ? inviteToken : undefined)
      .then((response) => {
        updateAuthData(!!response.data.id);
        updateUserData(response.data);
        addCohereIdentity(response.data);
        addLogRocketIdentity(response.data);

        const isOwner = response.data.role === USER_ROLE.OWNER;

        enableShowWelcome && isOwner
          ? navigate('/welcome', { replace: true })
          : navigate('/', { replace: true });
      })
      .catch((error) => alert(error.message))
      .finally(() => {
        updateDataLoading(false);
      });
  };

  const { values, errors, touched, dirty, handleChange, handleSubmit, validateForm, setValues } =
    useFormik({
      initialValues: emptyValues,
      validationSchema: SignUpFormSchema,
      validateOnMount: false,
      validateOnBlur: false,
      validateOnChange: false,
      onSubmit: (values: SignUpFormValues, { resetForm }): void => {
        signUpSubmit(values);

        resetForm({ values: emptyValues });
      }
    });

  return (
    <form
      onSubmit={async (e: React.FormEvent<HTMLFormElement> | undefined): Promise<any> => {
        e?.preventDefault();
        const formErrors = await validateForm();
        if (Object.keys(formErrors).length > 0) {
          return addNotification({
            message: Object.values(formErrors)[0] || 'Something went wrong',
            variant: 'error'
          });
        }

        handleSubmit(e);
      }}
    >
      <div className="sign-up__wrapper tid-sign-up__wrapper">
        <div className="sign-up__form-section">
          <div className="sign-in__form-logo">
            <TbLogo />
          </div>

          <TwTitle type={TwTitleType.secondary} text="Sign Up" />

          <div className="sign-up__inputs-container">
            <div className="sign-up__two-column">
              <div className="sign-up__two-column-item">
                <div className="tid-sign-up__first-name">
                  <TwField
                    id="first_name"
                    name="firstName"
                    value={values.firstName}
                    label="First name"
                    inputType={TwInputTypes.text}
                    disabled={false}
                    onInputChange={handleChange}
                    error={!!errors.firstName}
                    helperText={touched && dirty ? errors.firstName : ''}
                  />
                </div>
              </div>

              <div className="sign-up__two-column-item">
                <div className="tid-sign-up__last-name">
                  <TwField
                    id="last_name"
                    name="lastName"
                    value={values.lastName}
                    label="Last name"
                    inputType={TwInputTypes.text}
                    onInputChange={handleChange}
                    error={!!errors.lastName}
                    helperText={touched && dirty ? errors.lastName : ''}
                  />
                </div>
              </div>
            </div>

            <div className="tid-sign-up__email">
              <TwField
                id="email"
                name="email"
                value={values.email}
                label="Email"
                inputType={TwInputTypes.text}
                disabled={inviteToken && !tokenNotFound ? true : false}
                onInputChange={handleChange}
                error={!!errors.email}
                helperText={touched && dirty ? errors.email : ''}
              />
            </div>

            {role !== USER_ROLE.CUSTOMER_CONTACT && (
              <div className="tid-sign-up__phone">
                <TwField
                  id="phone"
                  name="phone"
                  value={values.phone}
                  label="Phone number"
                  inputType={TwInputTypes.number}
                  placeholder="Optional"
                  disabled={inviteToken && !tokenNotFound ? true : false}
                  onInputChange={handleChange}
                />
              </div>
            )}

            <div className="tid-sign-up__company-name">
              <TwField
                id="company_name"
                name="companyName"
                value={values.companyName}
                label="Company name"
                inputType={TwInputTypes.text}
                disabled={inviteToken && !tokenNotFound ? true : false}
                onInputChange={handleChange}
                error={!!errors.companyName}
                helperText={touched && dirty ? errors.companyName : ''}
              />
            </div>

            {role !== USER_ROLE.CUSTOMER_CONTACT && (
              <div className="tid-sign-up__usdot">
                <TwField
                  id="usdot"
                  name="usdot"
                  value={values.usdot}
                  label="USDOT #"
                  inputType={TwInputTypes.text}
                  placeholder="Optional"
                  disabled={inviteToken && !tokenNotFound ? true : false}
                  onInputChange={handleChange}
                  error={!!errors.usdot}
                  helperText={touched && dirty ? errors.usdot : ''}
                />
              </div>
            )}

            <div className="tid-sign-up__password">
              <TwField
                id="password"
                name="password"
                value={values.password}
                label="Password"
                inputType={TwInputTypes.password}
                onInputChange={handleChange}
                error={!!errors.password}
                helperText={touched && dirty ? errors.password : ''}
              />
            </div>

            <div className="sign-up__action-container">
              <span className="sign-up__agreement tid-sign-up__agreement">
                By signing up, I agree to the Truckbase
                <a href={privacyUrl} target="_blank" rel="noreferrer">
                  {' '}
                  Privacy Policy{' '}
                </a>
                and{' '}
                <a href={termsUrl} target="_blank" rel="noreferrer">
                  Terms of Service
                </a>
                .
              </span>

              <div className="sign-up__submit-button tid-sign-up__submit-button">
                <TwButton
                  label="Sign Up"
                  disabled={false}
                  fullwidth
                  submit={true}
                  type={TwButtonType.primary}
                  action={handleSubmit}
                />
              </div>

              <span className="sign-up__already-member tid-sign-up__already-member">
                Already a member? <Link to="/signin"> Sign in</Link>.
              </span>
            </div>
          </div>
        </div>

        <div className="sign-up__photo-section">
          <div className="sign-up__logo-container">
            <TruckbaseLogo className="sign-up__logo" />
          </div>

          <TwTitle type={TwTitleType.primary} text="More trucking." />
          <TwTitle type={TwTitleType.primary} color={TwTitleColor.red} text="Less paperwork." />
        </div>
      </div>
    </form>
  );
};

export default SignUp;
